import { useCallback, useRef, useState } from 'react';
import { BACKEND_ORIGIN } from '../config';

function deepgramProxyWsUrl() {
  const wsOrigin = BACKEND_ORIGIN.replace(/^http/i, 'ws');
  return `${wsOrigin}/api/integrations/deepgram/live`;
}

function pickRecorderMimeType() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  for (const t of candidates) {
    if (!t || (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t))) {
      return t || undefined;
    }
  }
  return undefined;
}

function transcriptFromResults(data) {
  if (data?.type !== 'Results') return { text: '', isFinal: false };
  const alt = data?.channel?.alternatives?.[0];
  if (!alt) return { text: '', isFinal: false };
  const text = String(alt.transcript || '').trim();
  const isFinal = Boolean(data.is_final || data.speech_final);
  return { text, isFinal };
}

/**
 * Live Dutch dictation via backend WebSocket proxy (API key stays on server).
 * @param {{ onDraftChange: (text: string) => void, getDraft: () => string }} options
 */
export function useDeepgramDictation({ onDraftChange, getDraft }) {
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const sessionBaseRef = useRef('');
  const finalsRef = useRef('');
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const cleanupMedia = useCallback(() => {
    const rec = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const cleanupSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    try {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'CloseStream' }));
      }
    } catch {
      /* ignore */
    }
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }, []);

  const stop = useCallback(() => {
    cleanupMedia();
    cleanupSocket();
    setListening(false);
    setBusy(false);
    finalsRef.current = '';
    sessionBaseRef.current = '';
  }, [cleanupMedia, cleanupSocket]);

  const applyDraft = useCallback(
    (interim) => {
      const base = sessionBaseRef.current;
      const finals = finalsRef.current;
      let out = base;
      if (finals) {
        const sep = out && !/\s$/.test(out) ? ' ' : '';
        out = `${out}${sep}${finals}`;
      }
      if (interim) {
        const sep = out && !/\s$/.test(out) ? ' ' : '';
        out = `${out}${sep}${interim}`;
      }
      onDraftChange(out);
    },
    [onDraftChange]
  );

  const start = useCallback(async () => {
    if (listening || busy) return;
    if (typeof window === 'undefined' || !navigator?.mediaDevices?.getUserMedia) {
      throw new Error('microphone_unavailable');
    }
    setBusy(true);
    try {
      sessionBaseRef.current = getDraft() || '';
      finalsRef.current = '';

      await new Promise((resolve, reject) => {
        let settled = false;
        const fail = (err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          reject(err);
        };
        const ws = new WebSocket(deepgramProxyWsUrl());
        wsRef.current = ws;
        const timeout = setTimeout(() => {
          fail(new Error('deepgram_timeout'));
          ws.close();
        }, 15000);

        ws.onopen = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          resolve();
        };
        ws.onerror = () => fail(new Error('deepgram_ws_error'));
        ws.onclose = (ev) => {
          if (!settled) {
            if (ev.code === 4401) fail(new Error('not_authenticated'));
            else fail(new Error('deepgram_ws_error'));
          }
          setListening(false);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const { text, isFinal } = transcriptFromResults(data);
            if (!text) return;
            if (isFinal) {
              const prev = finalsRef.current;
              const sep = prev && !/\s$/.test(prev) ? ' ' : '';
              finalsRef.current = `${prev}${sep}${text}`.trim();
              applyDraft('');
            } else {
              applyDraft(text);
            }
          } catch {
            /* ignore non-JSON frames */
          }
        };
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.addEventListener('dataavailable', (event) => {
        const ws = wsRef.current;
        if (event.data.size > 0 && ws && ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      });
      mediaRecorder.start(250);
      setListening(true);
    } finally {
      setBusy(false);
    }
  }, [applyDraft, busy, getDraft, listening]);

  const toggle = useCallback(async () => {
    if (listening) {
      stop();
      return;
    }
    try {
      await start();
    } catch (e) {
      stop();
      throw e;
    }
  }, [listening, start, stop]);

  return { listening, busy, start, stop, toggle };
}
